/*
 * Purpose: Stateless UI controls used by the Access Shell layout (tabs, flag, avatar).
 * Boundary: Presentational only; consumes props; no internal state; a11y roles/labels provided.
 * Invariants:
 * - TabButton exposes ARIA-compliant tab semantics and defers selection state to props.
 * - SAFlag and NeutralAvatar are decorative SVGs that include accessible labels.
 */

// Component TabButton:
// Accessible tab button with selected styling
export function TabButton({ id, selected, controls, onClick, children }) {
    // Rendering - ARIA tab with selected state; visual styles reflect selection
    return (
        <button
            id={id}
            role="tab"
            aria-selected={selected}
            aria-controls={controls}
            onClick={onClick}
            className={
                "px-2.5 py-1 md:px-5 md:py-1.5 rounded-md md:rounded-lg text-xs md:text-sm font-medium transition-all touch-manipulation " +
                (selected
                    ? "bg-emerald-600 text-white shadow-[0_1px_0_rgba(255,255,255,.08)_inset]"
                    : "text-neutral-300 hover:text-white")
            }
            type="button"
        >
            {children}
        </button>
    );
}

// Component CountryHeader:
// Renders platform heading with Oaths branding (replaces old "Republic of South Africa" label)
export function CountryHeader({ label = "Your Digital Commissioner of Oaths" }) {
    return (
        <div className="md:contents">
            <h1 className="hidden md:block text-xl font-semibold leading-tight">
                <span>{label}</span>
            </h1>
            <div className="md:hidden">
                <img
                    src="/assets/images/oaths-logo.png"
                    alt="Oaths.co.za"
                    className="h-[4em] w-auto"
                />
            </div>
        </div>
    );
}

// Component NeutralAvatar:
// Neutral placeholder avatar SVG
export function NeutralAvatar({ className = "" }) {
    // Rendering - circular gradient backdrop with face glyphs
    return (
        <svg viewBox="0 0 128 128" className={className} role="img" aria-label="Neutral avatar">
            <defs>
                {/* Background gradient - subtle tonal variation */}
                <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#2a2a2a" />
                    <stop offset="1" stopColor="#3a3a3a" />
                </linearGradient>
            </defs>

            {/* Outer circle - avatar boundary */}
            <circle cx="64" cy="64" r="64" fill="url(#g)" />
            {/* Head */}
            <circle cx="64" cy="50" r="22" fill="#b0b0b0" />
            {/* Shoulders */}
            <path d="M24 108c6-22 28-32 40-32s34 10 40 32" fill="#b0b0b0" />
        </svg>
    );
}
